import { clsx } from 'clsx';
import { useMasterDocumentTranslation } from './i18n';
import { useMasterDocumentFormatters } from './formatters';
import type { FactBoxPaymentTermsProps, PeriodMode } from './MasterDocument.types';

const periodModeTranslationKeys: Record<PeriodMode, string> = {
  days: 'master_document.factBox.paymentTerms.periodDays',
  months: 'master_document.factBox.paymentTerms.periodMonths',
  custom: 'master_document.factBox.paymentTerms.periodCustom',
};

function Row({ label, value, mono, accent }: { label: string; value: string | number; mono?: boolean; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-content-tertiary dark:text-dark-content-secondary">{label}</span>
      <span
        className={clsx(
          'text-xs font-semibold',
          mono && 'tabular-nums',
          accent
            ? 'text-primary-custom-600 dark:text-primary-custom-400'
            : 'text-content-primary dark:text-dark-content-primary',
        )}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Widget de condiciones de pago para el panel FactBox.
 *
 * Muestra el código y nombre del plazo, número de cuotas, modo de período y
 * días de vencimiento. Cuando el pronto pago está habilitado, presenta una
 * sección con indicador visual verde, ventana en días y porcentaje de descuento.
 * Si existe anticipo requerido, lo muestra con su monto calculado.
 *
 * @see docs/colors.md - Sistema de tokens de color
 * @see MasterDocument.types - FactBoxPaymentTermsProps, PeriodMode
 *
 * @example
 * ```tsx
 * <FactBoxPaymentTerms
 *   data={{
 *     code: 'C30',
 *     name: 'Crédito 30 días',
 *     installmentCount: 1,
 *     periodMode: 'days',
 *     dueDays: 30,
 *     earlyPaymentEnabled: true,
 *     earlyPaymentDays: 10,
 *     earlyPaymentDiscountPct: 2,
 *   }}
 * />
 * ```
 */
export function FactBoxPaymentTerms({ data, className }: FactBoxPaymentTermsProps) {
  const { t } = useMasterDocumentTranslation();
  const { formatCurrency } = useMasterDocumentFormatters();
  return (
    <div className={clsx('flex flex-col gap-0.5', className)}>
      {/* Header: code + name */}
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-bold text-primary-custom-600 dark:text-primary-custom-400">
          {data.code}
        </span>
        <span className="text-xs font-semibold text-content-primary dark:text-dark-content-primary">
          {data.name}
        </span>
      </div>

      <Row label={t('master_document.factBox.paymentTerms.installments')} value={data.installmentCount} mono />
      <Row label={t('master_document.factBox.paymentTerms.mode')} value={t(periodModeTranslationKeys[data.periodMode])} />
      <Row label={t('master_document.factBox.paymentTerms.dueDays')} value={data.dueDays} mono />

      {/* Early payment section */}
      {data.earlyPaymentEnabled && (
        <>
          <div className="h-px bg-border-secondary dark:bg-dark-border-primary my-1.5" />
          {/* Color semántico verde para estado activo. Migrar a token success-* cuando exista */}
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-green-600 dark:bg-green-400" aria-hidden="true" />
            <span className="text-xs font-semibold text-green-700 dark:text-green-400">{t('master_document.factBox.paymentTerms.earlyPaymentEnabled')}</span>
          </div>
          {data.earlyPaymentDays != null && (
            <Row label={t('master_document.factBox.paymentTerms.window')} value={t('master_document.factBox.paymentTerms.days', { count: data.earlyPaymentDays })} mono />
          )}
          {data.earlyPaymentDiscountPct != null && (
            <Row label={t('master_document.factBox.paymentTerms.discount')} value={`${data.earlyPaymentDiscountPct}%`} mono accent />
          )}
        </>
      )}

      {/* Advance payment */}
      {(data.advancePaymentPct != null && data.advancePaymentPct > 0) && (
        <>
          <div className="h-px bg-border-secondary dark:bg-dark-border-primary my-1.5" />
          <Row label={t('master_document.factBox.paymentTerms.advanceRequired')} value={`${data.advancePaymentPct}%`} mono accent />
          {data.advancePaymentAmount != null && (
            <Row label={t('master_document.factBox.paymentTerms.advanceAmount')} value={formatCurrency(data.advancePaymentAmount)} mono />
          )}
        </>
      )}
    </div>
  );
}
